const data = {
  slug: "create-enterprise-value",
  number: "04",
  title: "Create Enterprise Value",
  intro: [
    "A larger business is not automatically a more valuable one. Revenue can grow while margins weaken. Market presence can expand while complexity increases. The business can become busier, larger and more demanding, without becoming more profitable, predictable or strategically valuable.",
    "Enterprise value is created when growth improves the quality and strength of the business, not merely its size.",
  ],
  sections: [
    {
      kicker: "What it looks like",
      heading: "The business is growing. The value may not be.",
      body: [
        "Leadership may be delivering revenue, launching initiatives and entering new markets, but the outcomes are not translating into sufficient profitability, predictability or long-term enterprise strength. You may be seeing:",
      ],
      items: [
        "Revenue increasing while margins remain under pressure",
        "Growth requiring disproportionate investment",
        "High dependence on a few customers, channels or products",
        "Working-capital pressure rising with scale",
        "Expansion creating more complexity than value",
        "Business performance varying sharply across periods",
        "Too many initiatives competing for capital and attention",
        "Strong market opportunity without a credible value-creation plan",
        "A board or investor seeking greater strategic clarity",
        "Leadership unable to connect growth priorities with financial outcomes",
        "The business overly dependent on the founder or key individuals",
        "An upcoming investment, transaction or succession requiring stronger fundamentals",
      ],
    },
    {
      kicker: "The real problem",
      heading: "Revenue is not the same as value.",
      body: [
        "A business may increase sales through discounting, high acquisition costs or operational complexity; enter more markets without repeatability; add products without strengthening the portfolio; or depend on a few individuals. The top line may keep moving.",
        "Value is created when sustainable profitability, predictable performance, strategic focus, repeatable growth, defensible advantage, leadership depth, operating discipline, lower concentration risk and stronger cash generation reinforce one another.",
      ],
    },
    {
      kicker: "Possible causes",
      heading: "What may actually be limiting enterprise value",
      body: ["The constraint may sit beyond the financial statements."],
      items: [
        "Growth lacks strategic focus",
        "Revenue quality is weak",
        "The portfolio is diluting investment",
        "The commercial model is fragile",
        "Performance is unpredictable",
        "Growth increases complexity",
        "The organisation is key-person dependent",
        "Systems and governance are weak",
        "Strategic advantage is unclear",
        "Capital is not being allocated deliberately",
      ],
    },
    {
      kicker: "What boards need to see",
      heading: "A growth story becomes credible when the choices are clear.",
      body: [
        "Boards and investors need more than ambition. They need to understand where growth will come from, why the business can win, how growth will improve economics, what must change operationally, what the business will stop doing, how risk will be reduced and how progress will be measured. A credible value-creation plan connects strategy, economics and execution.",
      ],
    },
    {
      kicker: "Common mistakes",
      heading: "Value is weakened when growth is pursued without enough choice.",
      items: [
        "Focus only on revenue growth",
        "Optimise short-term profit at the expense of future capability",
        "Pursue every available opportunity",
        "Delay portfolio decisions",
        "Build the plan only through financial targets",
        "Treat investor readiness as a presentation exercise",
        "Depend on a few individuals",
        "Add governance without improving decisions",
        "Separate strategy from capital allocation",
        "Postpone execution until the plan is complete",
      ],
    },
    {
      kicker: "Our approach",
      heading: "How SWITCH diagnoses enterprise value",
      body: [
        "We examine what is driving value, and what is preventing it from compounding. The diagnostic is shaped around the business and may examine:",
      ],
      items: [
        "Strategic position",
        "Revenue quality",
        "Profitability and economics",
        "Operating leverage",
        "Cash generation",
        "Portfolio performance",
        "Commercial effectiveness",
        "Operations and scalability",
        "Organisation and leadership",
        "Governance and execution",
        "Capital allocation",
        "Risk management",
      ],
    },
    {
      kicker: "From founder value to enterprise value",
      heading: "The business must perform beyond the founder's direct involvement.",
      body: [
        "This does not mean removing the founder's judgement, ambition or influence. It means institutionalising customer relationships, distributing decision-making appropriately, extending leadership capability beyond one person, making performance data visible and ensuring the business can grow without every exception returning to the founder.",
        "SWITCH helps convert founder-created strength into organisational strength.",
      ],
    },
    {
      kicker: "Potential interventions",
      heading: "What will strengthen enterprise value",
      body: ["The intervention depends on what will strengthen enterprise value."],
      items: [
        "Defining the value-creation strategy",
        "Prioritising the portfolio",
        "Improving revenue quality",
        "Strengthening pricing and profitability",
        "Redesigning the commercial model",
        "Simplifying the operating model",
        "Building scalable systems",
        "Reducing founder and key-person dependency",
        "Building leadership and organisational capability",
        "Improving governance",
        "Preparing for investment or transaction",
      ],
    },
    {
      kicker: "From plan to execution",
      heading: "We stay to make it happen",
      body: [
        "Enterprise value is built through operating decisions made repeatedly over time. SWITCH works alongside leadership, internal teams and existing partners, translating the thesis into functional priorities, aligning strategic and financial plans, improving pricing and margins, strengthening governance and correcting the plan as evidence emerges, until the agreed priorities are implemented.",
      ],
    },
  ],
};

export default data;
